import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { PageTitle, Empty } from "@/components/app/ui";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const Block = ({ t, items }) => (Array.isArray(items) && items.length ? (
  <div><div className="font-mono text-[10px] uppercase tracking-widest text-[#7c3aed] mb-2">{t}</div><ul className="list-disc pl-5 space-y-1 text-sm">{items.map((x, i) => <li key={i}>{String(x)}</li>)}</ul></div>
) : null);

export default function Digests() {
  const { user, company } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/app/digests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const run = async () => { setBusy(true); try { await api.post("/app/digests/run"); toast.success(`Digest generated and emailed to ${company?.owner_email}`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div data-testid="digests-page">
      <PageTitle title="Weekly Digest" copy="Every Monday at 8:00 AM IST the company owner receives an AI-written summary of last week's marketing, industry news, upcoming events, competitor changes, end-customer activity (clicks, form fills, leads) and what to try next.">
        {["owner", "admin"].includes(user?.role) && <button disabled={busy || company?.plan === "free"} onClick={run} className="btn-brand disabled:opacity-50" data-testid="digest-run-btn"><Send className="h-4 w-4" />{busy ? "Generating..." : "Send this week's digest now"}</button>}
      </PageTitle>
      {company?.plan === "free" && <p className="mb-6 text-sm text-muted-foreground">Weekly digests are included with paid plans.</p>}
      {items.length === 0 && <Empty title="No digests yet" copy="Your first digest arrives next Monday, or send one now." />}
      <div className="space-y-6">
        {items.map((d) => (
          <article key={d.id} className="card-box p-6 md:p-8 space-y-5" data-testid={`digest-${d.id}`}>
            <div className="flex flex-wrap justify-between gap-2"><h2 className="text-2xl font-bold">{d.data.headline || "Weekly digest"}</h2><span className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleString()} · sent to {d.sent_to}</span></div>
            <p className="text-muted-foreground">{d.data.summary}</p>
            <div className="flex flex-wrap gap-2 text-xs">{Object.entries(d.stats).map(([k, v]) => <span key={k} className="rounded-full bg-foreground/5 px-3 py-1 capitalize">{k}: <strong>{v}</strong></span>)}</div>
            <div className="grid md:grid-cols-2 gap-5">
              <Block t="What's new in your industry" items={d.data.industry_news} />
              <Block t="Upcoming events" items={d.data.events} />
              <Block t="Competitor watch" items={d.data.competitors} />
              <div><div className="font-mono text-[10px] uppercase tracking-widest text-[#7c3aed] mb-2">Your customers</div><p className="text-sm">{d.data.customer_activity}</p></div>
            </div>
            <Block t="What to try next" items={d.data.try_next} />
          </article>
        ))}
      </div>
    </div>
  );
}
